import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ShieldAlertIcon, AlertOctagonIcon, HeartPulseIcon, DropletIcon, ActivityIcon, ThermometerIcon, WindIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon, FileTextIcon, PhoneIcon, IdCardIcon, CalendarIcon, PrinterIcon, ClipboardPlusIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { getPatient, type Vital, type Encounter } from '../data/patients';
const severityTone = {
  Mild: 'warning',
  Moderate: 'warning',
  Severe: 'danger',
  Anaphylaxis: 'danger'
} as const;
const vitalIcon: Record<string, React.ReactNode> = {
  'Heart Rate': <HeartPulseIcon className="h-4 w-4" data-id="element-3017" data-mp="3bkqtc9flkt1c" />,
  'Blood Pressure': <ActivityIcon className="h-4 w-4" data-id="element-3018" data-mp="3bj7fepheyu2q" />,
  'SpO₂': <DropletIcon className="h-4 w-4" data-id="element-3019" data-mp="1bl4zx3y6sd9v" />,
  Temp: <ThermometerIcon className="h-4 w-4" data-id="element-3020" data-mp="1bm6ma4knkm6w" />,
  'Resp Rate': <WindIcon className="h-4 w-4" data-id="element-3021" data-mp="1gkrdjehxfvj7" />,
  'Blood Glucose': <DropletIcon className="h-4 w-4" data-id="element-3022" data-mp="1gmly2rpadlmr" />
};
const flagCls = {
  critical: 'ring-red-200 bg-red-50',
  high: 'ring-amber-200 bg-amber-50',
  low: 'ring-sky-200 bg-sky-50'
} as const;
const encTone = {
  ER: 'danger',
  OPD: 'info',
  IPD: 'brand',
  Daycare: 'purple',
  'Video consultation': 'neutral'
} as const;
const tabs = ['Overview', 'Encounters', 'Clinical (EMR)', 'Medications', 'Documents'] as const;
export function PatientProfile() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const patient = getPatient(id || '');
  const [tab, setTab] = useState<(typeof tabs)[number]>('Overview');
  if (!patient) {
    return <div className="p-6" data-id="element-3023" data-mp="2e7qgg6z7pzpj">
        <p className="text-slate-500" data-id="element-3024" data-mp="2e9kz1en0hfqg">Patient not found.</p>
        <Button className="mt-3" variant="outline" onClick={() => navigate('/patients')} data-id="element-3025" data-mp="2i9ldgza41qxi">Back to MPI</Button>
      </div>;
  }
  return <div className="p-6 max-w-7xl" data-id="element-3026" data-mp="3r7l7govdnl6h">
      <button onClick={() => navigate('/patients')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-id="element-3027" data-mp="3r4fjxe2ecrcv">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-3028" data-mp="3i6nb8sr6khyg" /> Master Patient Index
      </button>

      {/* Safety banner — allergies & alerts always visible */}
      {(patient.allergies.length > 0 || patient.alerts.length > 0) && <motion.div initial={{
      opacity: 0,
      y: -6
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3" data-id="element-3029" data-mp="dtzxhbkr6fj4">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-red-700" data-id="element-3030" data-mp="qrzlzxee5v">
            <AlertOctagonIcon className="h-4 w-4" data-id="element-3031" data-mp="xfmkttqsik43" /> Safety Alerts
          </span>
          {patient.allergies.map(a => <Badge key={a.substance} tone="danger" icon={<ShieldAlertIcon className="h-3 w-3" data-id="element-3033" data-mp="sn36uvo6xmyr" />} data-id="element-3032" data-mp="sowiyqhkoze5">
              {a.substance} · {a.severity}
            </Badge>)}
          {patient.alerts.map(a => <Badge key={a} tone="warning" data-id="element-3034" data-mp="1506z5q4h9wlu">{a}</Badge>)}
        </motion.div>}

      {/* Identity banner */}
      <Card className="mb-4 p-5" data-id="element-3035" data-mp="19x7766kw19fl">
        <div className="flex flex-wrap items-start gap-5" data-id="element-3036" data-mp="1dzu24gcuvah0">
          <Avatar name={patient.name} src={patient.photo} size="lg" data-id="element-3037" data-mp="1e1exg86zuxfx" />
          <div className="min-w-0 flex-1" data-id="element-3038" data-mp="1iy5cqtaz5p0a">
            <div className="flex flex-wrap items-center gap-2" data-id="element-3039" data-mp="1ixcbu8yp5tq2">
              <h1 className="font-display text-xl font-bold text-slate-900" data-id="element-3040" data-mp="1nwynidyvn1wr">{patient.name}</h1>
              <Badge tone={patient.status === 'Emergency' ? 'danger' : patient.status === 'Admitted' ? 'brand' : 'info'} data-id="element-3041" data-mp="1sqb17t20poaq">
                {patient.status}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-600" data-id="element-3042" data-mp="21qpr75pxfrkk">
              <span className="inline-flex items-center gap-1.5" data-id="element-3043" data-mp="26psuakmcjnpw"><IdCardIcon className="h-4 w-4 text-slate-400" data-id="element-3044" data-mp="26meqd7y1b6gc" />{patient.mrn}</span>
              <span data-id="element-3045" data-mp="1t07ongf313e2">{patient.age} yrs · {patient.sex}</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-3046" data-mp="1p18jpvtcp6mm"><DropletIcon className="h-4 w-4 text-slate-400" data-id="element-3047" data-mp="1oycjg0u165v0" />{patient.bloodGroup}</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-3048" data-mp="22ty7o5sfwlmd"><CalendarIcon className="h-4 w-4 text-slate-400" data-id="element-3049" data-mp="1xsh7kchs536u" />DOB {patient.dob}</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-3050" data-mp="1a2mvvy975ko6"><PhoneIcon className="h-4 w-4 text-slate-400" data-id="element-3051" data-mp="1a3g7pdn0j0d6" />{patient.phone}</span>
            </div>
            {patient.location && <p className="mt-2 text-sm font-medium text-brand-700" data-id="element-3052" data-mp="1k12oy6rsb40b">{patient.location}</p>}
          </div>
          <div className="flex flex-col gap-2" data-id="element-3053" data-mp="1f1zu1f5iqe62">
            <Button icon={<ClipboardPlusIcon className="h-4 w-4" data-id="element-3055" data-mp="1f4lz984xshl4" />} data-id="element-3054" data-mp="1f4ca1mrpuegh">New Order</Button>
            <Button variant="outline" size="sm" icon={<PrinterIcon className="h-4 w-4" data-id="element-3057" data-mp="2qhohe7mopf7e" />} data-id="element-3056" data-mp="2vey61l291mu6">Print summary</Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-slate-200 overflow-x-auto" data-id="element-3058" data-mp="c8lx2lzkd6b3">
        {tabs.map(t => <button key={t} onClick={() => setTab(t)} className={`relative whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${tab === t ? 'text-brand-700' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-3059" data-mp="h9jdat10jq9y">
            {t}
            {tab === t && <motion.span layoutId="tab" className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand-600" data-id="element-3060" data-mp="w00fielh2k7w" />}
          </button>)}
      </div>

      {tab === 'Overview' && <Overview patient={patient} data-id="element-3061" data-mp="m60dwmpefx3x" />}
      {tab === 'Encounters' && <Encounters encounters={patient.encounters} data-id="element-3062" data-mp="r5mqqv1bfbc7" />}
      {tab !== 'Overview' && tab !== 'Encounters' && <EmrTab title={tab} data-id="element-3063" data-mp="1euxl9us2o9hw" />}
    </div>;
}
function VitalCard({
  v
}: {
  v: Vital;
}) {
  const TrendIcon = v.trend === 'up' ? TrendingUpIcon : v.trend === 'down' ? TrendingDownIcon : MinusIcon;
  return <div className={`rounded-lg p-3 ring-1 ${v.flag ? flagCls[v.flag] : 'ring-slate-200 bg-white'}`} data-id="element-3064" data-mp="1tifpyt5mx44b">
      <div className="flex items-center justify-between text-slate-400" data-id="element-3065" data-mp="1oi1ibc7y3w6t">
        <span className={v.flag ? 'text-slate-600' : ''} data-id="element-3066" data-mp="1jjq5qtq9n7kw">{vitalIcon[v.label]}</span>
        <TrendIcon className={`h-3.5 w-3.5 ${v.flag === 'critical' ? 'text-red-500' : v.flag === 'high' ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-3067" data-mp="1ekwtf4mptmmb" />
      </div>
      <p className="mt-1.5 text-lg font-bold font-display text-slate-900 tabular-nums leading-none" data-id="element-3068" data-mp="15tms2r5gcm72">
        {v.value}<span className="ml-1 text-xs font-normal text-slate-400" data-id="element-3069" data-mp="10ua5m7ihth50">{v.unit}</span>
      </p>
      <p className="mt-1 text-[11px] font-medium text-slate-500" data-id="element-3070" data-mp="vvgsig74bs5d">{v.label}</p>
    </div>;
}
function Overview({
  patient
}: {
  patient: NonNullable<ReturnType<typeof getPatient>>;
}) {
  return <div className="grid gap-4 lg:grid-cols-3" data-id="element-3071" data-mp="qy6wuit4yk93">
      <div className="lg:col-span-2 space-y-4" data-id="element-3072" data-mp="mvtrvwm7qqdd">
        <Card className="pb-4" data-id="element-3073" data-mp="mx4uht3x9s2w">
          <CardHeader title="Latest Vitals" subtitle="Recorded 14:20 · Nursing" icon={<HeartPulseIcon className="h-4 w-4" data-id="element-3075" data-mp="32jpjs1njrq1g" />} data-id="element-3074" data-mp="2yivtj1mmtz5e" />
          <div className="mt-3 grid grid-cols-2 gap-2.5 px-5 sm:grid-cols-3" data-id="element-3076" data-mp="32i4lc0y43myb">
            {patient.vitals.map(v => <VitalCard key={v.label} v={v} data-id="element-3077" data-mp="37gpmc2l6uant" />)}
          </div>
        </Card>

        <Card className="pb-4" data-id="element-3078" data-mp="3ch3vjnyizqnf">
          <CardHeader title="Recent Encounters" subtitle="Longitudinal record across all facilities" icon={<CalendarIcon className="h-4 w-4" data-id="element-3080" data-mp="2jt7u65y0hakb" />} data-id="element-3079" data-mp="2eueo2yl20lu6" />
          <div className="mt-2 px-2 pb-1" data-id="element-3081" data-mp="2jtzl33gnyk49">
            {patient.encounters.slice(0, 3).map(e => <div key={e.id} className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50" data-id="element-3082" data-mp="2oqq3vyjurttd">
                <Badge tone={encTone[e.type]} data-id="element-3083" data-mp="2tkc58ukkgb9v">{e.type}</Badge>
                <div className="min-w-0 flex-1" data-id="element-3084" data-mp="1w4f2wmsc3qw1">
                  <p className="text-sm font-medium text-slate-900 truncate" data-id="element-3085" data-mp="1w1j4yyghl27l">{e.diagnosis} <span className="font-mono text-xs text-slate-400" data-id="element-3086" data-mp="2115il9088kgx">· {e.icd10}</span></p>
                  <p className="text-xs text-slate-500" data-id="element-3087" data-mp="ug75r3jh89sg">{e.department} · {e.provider}</p>
                </div>
                <span className="text-xs text-slate-400 shrink-0" data-id="element-3088" data-mp="phvwatx3g1ar">{e.date}</span>
              </div>)}
          </div>
        </Card>
      </div>

      <div className="space-y-4" data-id="element-3089" data-mp="yh9au102rg2c">
        <Card className="p-5" data-id="element-3090" data-mp="yg7xfpvl6hhg">
          <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-3091" data-mp="yj3rh34bcm0m">Allergies</h3>
          {patient.allergies.length === 0 ? <p className="text-sm text-slate-400" data-id="element-3092" data-mp="18ffbkfa5ag5m">No known allergies (NKDA)</p> : <div className="space-y-2.5" data-id="element-3093" data-mp="1m6bybphe434r">
              {patient.allergies.map(a => <div key={a.substance} className="rounded-lg border border-red-100 bg-red-50/50 p-3" data-id="element-3094" data-mp="1i4yqj9uqlpv7">
                  <div className="flex items-center justify-between" data-id="element-3095" data-mp="1w4i0m873zba5">
                    <span className="text-sm font-semibold text-slate-900" data-id="element-3096" data-mp="1w2ne4pg6vb3y">{a.substance}</span>
                    <Badge tone={severityTone[a.severity]} data-id="element-3097" data-mp="1r7hq9l387md8">{a.severity}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-600" data-id="element-3098" data-mp="2apdoegbw4e1o">{a.category} · {a.reaction}</p>
                </div>)}
            </div>}
        </Card>

        <Card className="p-5" data-id="element-3099" data-mp="20shf48e63nw2">
          <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-3100" data-mp="20vdfe3dhmono">Chronic Conditions</h3>
          {patient.chronic.length === 0 ? <p className="text-sm text-slate-400" data-id="element-3101" data-mp="1s0ffgcbxabmu">None recorded</p> : <div className="flex flex-wrap gap-1.5" data-id="element-3102" data-mp="1wrfcdhl1ktpc">
              {patient.chronic.map(c => <Badge key={c} tone="neutral" data-id="element-3103" data-mp="1i31bvdb3lhby">{c}</Badge>)}
            </div>}
        </Card>

        <Card className="p-5" data-id="element-3104" data-mp="1n1cizga0ddqh">
          <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-3105" data-mp="1n2dxjuq8gicy">Coverage</h3>
          <dl className="space-y-2 text-sm" data-id="element-3106" data-mp="1935v8syrwu4j">
            <div className="flex justify-between" data-id="element-3107" data-mp="194gz0ss7y1vn"><dt className="text-slate-500" data-id="element-3108" data-mp="1d480bqfdg0ef">Payer</dt><dd className="font-medium text-slate-900" data-id="element-3109" data-mp="1d36hv0w10bmo">{patient.payer}</dd></div>
            <div className="flex justify-between" data-id="element-3110" data-mp="1du7wormzdxrc"><dt className="text-slate-500" data-id="element-3111" data-mp="1dx2esciiv6hl">National ID</dt><dd className="font-mono text-xs text-slate-700" data-id="element-3112" data-mp="1iucd6hnw2shk">{patient.nationalId}</dd></div>
          </dl>
        </Card>
      </div>
    </div>;
}
function Encounters({
  encounters
}: {
  encounters: Encounter[];
}) {
  return <Card className="p-5" data-id="element-3113" data-mp="18wero1xez9sn">
      <h3 className="mb-4 text-sm font-semibold text-slate-900" data-id="element-3114" data-mp="18xpw83yoolkt">Complete Encounter Timeline</h3>
      <div className="relative pl-6" data-id="element-3115" data-mp="1wnk4e8825lyq">
        <span className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" data-id="element-3116" data-mp="21l3npmjm4oxj" />
        {encounters.map(e => <div key={e.id} className="relative pb-6 last:pb-0" data-id="element-3117" data-mp="1ns487kjtyupz">
            <span className="absolute -left-[22px] top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-500 ring-1 ring-brand-200" data-id="element-3118" data-mp="1ruip3z3xvkem" />
            <div className="flex flex-wrap items-center gap-2" data-id="element-3119" data-mp="2fjjqj2ectvae">
              <Badge tone={encTone[e.type]} data-id="element-3120" data-mp="2kathc6bn34nv">{e.type}</Badge>
              <span className="text-sm font-semibold text-slate-900" data-id="element-3121" data-mp="2kd65j17zqb9d">{e.diagnosis}</span>
              <span className="font-mono text-xs text-slate-400" data-id="element-3122" data-mp="8u9oldktncx4">{e.icd10}</span>
              {e.status === 'Active' && <Badge tone="success" data-id="element-3123" data-mp="3wq25qdyztu3">Active</Badge>}
            </div>
            <p className="mt-1 text-xs text-slate-500" data-id="element-3124" data-mp="ikukgt9423cc">{e.date} · {e.department} · {e.provider}</p>
          </div>)}
      </div>
    </Card>;
}
function EmrTab({
  title
}: {
  title: string;
}) {
  return <Card className="p-10 text-center" data-id="element-3125" data-mp="3m3e6he174cyp">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600" data-id="element-3126" data-mp="3h3jch1m4j79f">
        <FileTextIcon className="h-6 w-6" data-id="element-3127" data-mp="3w0iu7iaowghm" />
      </div>
      <h3 className="mt-4 font-display text-base font-semibold text-slate-900" data-id="element-3128" data-mp="3w23m105mdlbs">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500" data-id="element-3129" data-mp="3r04jfshpesfw">
        Structured, ICD-10 / SNOMED / LOINC-coded longitudinal record renders here — SOAP notes,
        medication history, flowsheets and documents, all research-ready and versioned in the audit log.
      </p>
    </Card>;
}